// import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
// import PaginatedList from '@theme/paginated-list';

// /**
//  * A custom element that renders a pagniated results list
//  */
// export default class ResultsList extends PaginatedList {
//   connectedCallback() {
//     super.connectedCallback();

//     mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
//     this.setAttribute('initialized', '');
//   }

//   disconnectedCallback() {
//     mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
//   }

//   /**
//    * Updates the layout.
//    *
//    * @param {Event} event
//    */
//   updateLayout({ target }) {
//     if (!(target instanceof HTMLInputElement)) return;

//     this.#animateLayoutChange(target.value);
//   }

//   /**
//    * Sets the layout.
//    *
//    * @param {string} value
//    */
//   #animateLayoutChange = async (value) => {
//     const { grid } = this.refs;

//     if (!grid) return;

//     await startViewTransition(() => this.#setLayout(value), ['product-grid']);

//     requestIdleCallback(() => {
//       const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
//       sessionStorage.setItem(`product-grid-view-${viewport}`, value);
//     });
//   };

//   /**
//    * Animates the layout change.
//    *
//    * @param {string} value
//    */
//   #setLayout(value) {
//     const { grid } = this.refs;
//     if (!grid) return;
//     grid.setAttribute('product-grid-view', value);
//   }

//   /**
//    * Handles the media query change event.
//    *
//    * @param {MediaQueryListEvent} event
//    */
//   #handleMediaQueryChange = (event) => {
//     const targetElement = event.matches
//       ? this.querySelector('[data-grid-layout="desktop-default-option"]')
//       : this.querySelector('[data-grid-layout="mobile-option"]');

//     if (!(targetElement instanceof HTMLInputElement)) return;

//     targetElement.checked = true;
//     this.#setLayout('default');
//   };
// }

// if (!customElements.get('results-list')) {
//   customElements.define('results-list', ResultsList);
// }

import { requestIdleCallback } from '@theme/utilities';

export default class PaginatedList extends HTMLElement {
  #loading = false;
  #observer = null;
  #currentPage = 1;
  #loadedPages = new Set();
  #nextUrl = null;

  connectedCallback() {
    this.#init();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #init() {
    /*
     * Find the pagination navigation generated by Shopify.
     */
    const pagination = this.querySelector('nav.pagination');

    if (!pagination) {
      return;
    }

    /*
     * Find the "next" page link.
     */
    const nextLink = pagination.querySelector(
      'a[rel="next"], a.pagination__next, .pagination__item--next a'
    );

    if (!nextLink) {
      return;
    }

    this.#nextUrl = nextLink.href;

    /*
     * Mark the current page as loaded.
     */
    const currentPage = this.#getPageFromUrl(window.location.href);

    if (currentPage) {
      this.#currentPage = currentPage;
      this.#loadedPages.add(currentPage);
    }

    /*
     * Hide the normal pagination because we're using
     * infinite scrolling.
     */
    pagination.style.display = 'none';

    this.#createObserver();
  }

  #createObserver() {
    this.#observer?.disconnect();

    /*
     * Create a sentinel at the bottom of the results list.
     */
    let sentinel = this.querySelector('[data-pagination-sentinel]');

    if (!sentinel) {
      sentinel = document.createElement('div');

      sentinel.setAttribute(
        'data-pagination-sentinel',
        ''
      );

      sentinel.setAttribute(
        'aria-hidden',
        'true'
      );

      sentinel.style.width = '100%';
      sentinel.style.height = '1px';

      this.appendChild(sentinel);
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (!entry?.isIntersecting) {
          return;
        }

        this.#loadNextPage();
      },
      {
        rootMargin: '800px 0px'
      }
    );

    this.#observer.observe(sentinel);
  }

  async #loadNextPage() {
    if (this.#loading) {
      return;
    }

    if (!this.#nextUrl) {
      return;
    }

    const nextPage = this.#getPageFromUrl(this.#nextUrl);

    /*
     * Very important:
     * Don't request a page that has already been loaded.
     */
    if (nextPage && this.#loadedPages.has(nextPage)) {
      this.#updateNextUrlFromCurrentPage();

      return;
    }

    this.#loading = true;

    try {
      const url = new URL(this.#nextUrl, window.location.origin);

      /*
       * Make sure Shopify returns the current results page.
       */
      url.searchParams.set(
        'sections',
        this.dataset.sectionId || this.getAttribute('section-id') || ''
      );

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(
          `Pagination request failed: ${response.status}`
        );
      }

      const html = await response.text();

      /*
       * The response may contain the whole results-list
       * or just the section.
       */
      const parser = new DOMParser();

      const documentFragment = parser.parseFromString(
        html,
        'text/html'
      );

      let newResults = documentFragment.querySelector(
        'results-list'
      );

      if (!newResults) {
        newResults = documentFragment.querySelector(
          '[data-results-list]'
        );
      }

      if (!newResults) {
        /*
         * Shopify may return the section itself.
         */
        newResults = documentFragment.querySelector(
          `#shopify-section-${this.dataset.sectionId}`
        );
      }

      if (!newResults) {
        throw new Error(
          'Could not find results list in pagination response.'
        );
      }

      /*
       * Get the product grid from the current and new page.
       */
      const currentGrid =
        this.querySelector('.product-grid');

      const newGrid =
        newResults.querySelector('.product-grid');

      if (!currentGrid || !newGrid) {
        throw new Error(
          'Product grid could not be found.'
        );
      }

      /*
       * Get existing product IDs.
       */
      const existingProductIds = new Set(
        Array.from(
          currentGrid.querySelectorAll(
            '[data-product-id]'
          )
        )
          .map((item) =>
            item.getAttribute('data-product-id')
          )
          .filter(Boolean)
      );

      /*
       * Append ONLY products that don't already exist.
       */
      const newItems = Array.from(
        newGrid.querySelectorAll(
          '[data-product-id]'
        )
      );

      let addedItems = 0;

      newItems.forEach((item) => {
        const productId =
          item.getAttribute('data-product-id');

        if (!productId) {
          return;
        }

        /*
         * Prevent duplicate products.
         */
        if (existingProductIds.has(productId)) {
          return;
        }

        /*
         * Clone the complete existing product card.
         */
        const clonedItem = item.cloneNode(true);

        currentGrid.appendChild(clonedItem);

        existingProductIds.add(productId);

        addedItems++;
      });

      /*
       * Mark page as loaded.
       */
      if (nextPage) {
        this.#loadedPages.add(nextPage);
        this.#currentPage = nextPage;
      }

      /*
       * Find the next page from the NEW response.
       */
      const nextPagination =
        newResults.querySelector(
          'nav.pagination'
        );

      const nextLink =
        nextPagination?.querySelector(
          'a[rel="next"], a.pagination__next, .pagination__item--next a'
        );

      if (nextLink) {
        this.#nextUrl = nextLink.href;
      } else {
        /*
         * No more pages.
         */
        this.#nextUrl = null;

        this.#observer?.disconnect();
      }

      /*
       * If Shopify returned the same products,
       * don't repeatedly request the same page.
       */
      if (addedItems === 0 && this.#nextUrl) {
        const nextPageAfterLoad =
          this.#getPageFromUrl(this.#nextUrl);

        if (
          nextPageAfterLoad &&
          this.#loadedPages.has(nextPageAfterLoad)
        ) {
          this.#nextUrl = null;
          this.#observer?.disconnect();
        }
      }

      /*
       * Give Horizon/custom elements a chance to initialize
       * the newly added product cards.
       */
      requestIdleCallback(() => {
        this.#initializeNewContent();
      });
    } catch (error) {
      console.error(
        'Infinite scroll error:',
        error
      );
    } finally {
      this.#loading = false;
    }
  }

  #updateNextUrlFromCurrentPage() {
    if (!this.#nextUrl) {
      return;
    }

    const currentUrl =
      new URL(
        this.#nextUrl,
        window.location.origin
      );

    const currentPage =
      this.#getPageFromUrl(currentUrl.href);

    if (!currentPage) {
      return;
    }

    const nextPage = currentPage + 1;

    currentUrl.searchParams.set(
      'page',
      nextPage
    );

    this.#nextUrl = currentUrl.href;
  }

  #getPageFromUrl(url) {
    try {
      const parsedUrl =
        new URL(
          url,
          window.location.origin
        );

      const page =
        parseInt(
          parsedUrl.searchParams.get('page') || '1',
          10
        );

      return Number.isFinite(page)
        ? page
        : 1;
    } catch {
      return null;
    }
  }

  #initializeNewContent() {
    /*
     * Re-run custom element initialization where required.
     *
     * Horizon's custom elements normally initialize
     * automatically when inserted into the DOM.
     */

    const newElements =
      this.querySelectorAll(
        ':not([data-pagination-initialized])'
      );

    newElements.forEach((element) => {
      if (
        element instanceof HTMLElement
      ) {
        element.setAttribute(
          'data-pagination-initialized',
          ''
        );
      }
    });
  }
}